/**
 * @lodex-pro/web-admin/features/tenants/hooks — Task 25.2 (DEC-113).
 *
 * TanStack Query hooks over the PLATFORM_ADMIN tenant-management API
 * (/v1/platform/tenants). Mirrors the web-dispatch sites-operators hook idiom.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts, type EmptyBody } from '@lodex-pro/web-shared';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TenantSummary {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly tenantType: string;
  readonly haulerAbbreviation: string | null;
  readonly dotNumber: string | null;
  readonly primaryPhone: string | null;
  readonly primaryEmail: string | null;
  readonly businessAddress: string | null;
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly driverCount: number;
  readonly jobCount: number;
  readonly lastJobCompletedAt: string | null;
  readonly setupWizardCompletionPct: number;
  readonly setupWizardAllComplete: boolean;
  readonly setupWizardCompletedAt: string | null;
  readonly setupWizardStep: number;
}

export interface TenantOperator {
  readonly id: string;
  readonly name: string;
  readonly abbreviation: string;
  /** 0205 — the code its tickets print; Paelode's for a catalog customer (may differ). */
  readonly ticketCode?: string;
  readonly isPlatformOperator: boolean;
  readonly isActive: boolean;
}

export interface WizardSection {
  readonly section: number;
  readonly complete: boolean;
  readonly adminOverride: boolean;
  readonly effectiveComplete: boolean;
}

export interface TenantDetail {
  readonly tenant: TenantSummary;
  readonly operators: readonly TenantOperator[];
  readonly setupWizard: {
    readonly completionPct: number;
    readonly allComplete: boolean;
    readonly completedAt: string | null;
    readonly lastSection: number;
    readonly sections: readonly WizardSection[];
  };
  readonly activity: {
    readonly driverCount: number;
    readonly jobCount30d: number;
    readonly lastJobCompletedAt: string | null;
  };
}

export type StatusFilter = 'all' | 'active' | 'suspended';
export type TenantSort = 'name' | 'createdAt' | 'jobCount';

// ─── Query keys ──────────────────────────────────────────────────────────────

export const TENANTS_KEY = ['platform', 'tenants'] as const;
export const TENANT_KEY = (id: string): readonly string[] => ['platform', 'tenants', id];

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useTenants(
  status: StatusFilter,
  sort: TenantSort,
): ReturnType<typeof useApiQuery<{ tenants: readonly TenantSummary[] }>> {
  const opts = useApiOpts();
  const params = new URLSearchParams({ sort });
  if (status === 'active') params.set('isActive', 'true');
  if (status === 'suspended') params.set('isActive', 'false');
  return useApiQuery<{ tenants: readonly TenantSummary[] }>(
    opts,
    `/v1/platform/tenants?${params.toString()}`,
    { queryKey: [...TENANTS_KEY, status, sort], staleTime: 15_000 },
  );
}

export function useTenant(id: string): ReturnType<typeof useApiQuery<TenantDetail>> {
  const opts = useApiOpts();
  return useApiQuery<TenantDetail>(opts, `/v1/platform/tenants/${id}`, {
    queryKey: TENANT_KEY(id),
    staleTime: 15_000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export interface CreateTenantBody {
  name: string;
  slug: string;
  haulerAbbreviation: string;
  dotNumber?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  businessAddress?: string;
}

export function useCreateTenant(
  onDone: (id: string) => void,
): ReturnType<typeof useApiMutation<CreateTenantBody, { tenant: TenantSummary }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CreateTenantBody, { tenant: TenantSummary }>(
    opts,
    '/v1/platform/tenants',
    'POST',
    {
      onSuccess: (res) => {
        void qc.invalidateQueries({ queryKey: TENANTS_KEY });
        onDone(res.tenant.id);
      },
    },
  );
}

export interface UpdateTenantBody {
  name?: string;
  haulerAbbreviation?: string;
  dotNumber?: string | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  businessAddress?: string | null;
}

export function useUpdateTenant(
  id: string,
): ReturnType<typeof useApiMutation<UpdateTenantBody, { tenant: TenantSummary }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateTenantBody, { tenant: TenantSummary }>(
    opts,
    `/v1/platform/tenants/${id}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: TENANT_KEY(id) });
        void qc.invalidateQueries({ queryKey: TENANTS_KEY });
      },
    },
  );
}

/**
 * DEC-201 — the body type is `EmptyBody`, NOT `void`. With `void`, `mutate(undefined)`
 * is valid TypeScript and sends no body at all while still carrying a JSON
 * content-type, which the API answers with a 500. `EmptyBody` makes the caller
 * pass `{}`, so the trap is closed at compile time rather than by convention.
 */
function useTenantAction(
  id: string,
  action: 'suspend' | 'reactivate',
): ReturnType<typeof useApiMutation<EmptyBody, { tenant: { id: string; isActive: boolean } }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<EmptyBody, { tenant: { id: string; isActive: boolean } }>(
    opts,
    `/v1/platform/tenants/${id}/${action}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: TENANT_KEY(id) });
        void qc.invalidateQueries({ queryKey: TENANTS_KEY });
      },
    },
  );
}

export function useSuspendTenant(
  id: string,
): ReturnType<typeof useApiMutation<EmptyBody, { tenant: { id: string; isActive: boolean } }>> {
  return useTenantAction(id, 'suspend');
}

export function useReactivateTenant(
  id: string,
): ReturnType<typeof useApiMutation<EmptyBody, { tenant: { id: string; isActive: boolean } }>> {
  return useTenantAction(id, 'reactivate');
}
